import { TextField } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { makeStyles } from '@mui/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import 'react-phone-input-2/lib/style.css';
import facebookLoginButton from '../../assets/facebookLoginButton.svg';
import googleButton from '../../assets/google.svg';
import linkedinButton from '../../assets/linkedinButton.svg';
import openProjectBtn from '../../assets/openProjectBtn.svg';
import TiktokButton from '../../assets/TiktokButton.svg';
import twitterBtnIcon from '../../assets/twitterBtnIcon.svg';
import { IDataFromModal } from '../../pages/Workspace/interface';
import Appointment from '../Appointment';
import Autocomplete from '../AutoComplete';
import BrainTree from '../BrainTreeForm';
import Button from '../Button';
import CalculativeField from '../CalculativeField';
import CountryInput from '../Country';
import CurrencyInput from '../CurrencyInput';
import CustomRating from '../CustomRating';
import CustomTimePicker from '../CustomTimePicker';
import LazyImage from '../LazyImage';
import CustomPhoneInput from '../PhoneInput';
import Popup from '../Popup';
import Options from '../Slides/Options';
import TimeZone from '../Timezone';
import Input from './input';
import TinyMCEEditor from './richText';
import './style.scss';
import GeoLocation from './GeoLocation';
import Capture from './Capture';

type SlideType = {
  background: string,
  selectedData: any,
  landingPage: boolean,
  formState: any,
  masterType: any,
  setMasterType: (e: any) => void,
  selectedType: string,
  setSelectedType: (e: any) => void,
  fieldIndex: number | null,
  setFieldIndex: (e: any) => void,
  dataFromModal: IDataFromModal,
  inputType: string,
  editUUID?: string,
  previewType?: string,
  storeHistory: (field: string, value: any, type: string, optional?: any) => void,
  dropdown: boolean,
  setDropdown: (dropdown: any) => void
  setRedoState: (redoState: any) => void
  prevSelected: any,
  watermarkPosition: any,
  // using any since react-hook-form havent provided type for form validator method
  methods: any;
}

const useStyles = makeStyles(theme => ({
  menuPaper: {
    maxHeight: '180px',
    padding: "10px 10px"
  }
}));


const Slides: FC<SlideType> = ({ methods, landingPage, watermarkPosition, prevSelected, background, selectedData, formState, masterType, selectedType, fieldIndex, setFieldIndex, dataFromModal, inputType, editUUID, previewType, storeHistory, dropdown, setDropdown, setRedoState }) => {

  const classes = useStyles();
  const slidesRef = useRef<HTMLDivElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const hoverToolbarRef = useRef<HTMLDivElement | null>(null);
  const hoverToolbarTimer = useRef<number | null>(null);
  const hoverHideTimer = useRef<number | null>(null);
  const savedRange = useRef<Range | null>(null);
  const savedContainer = useRef<HTMLElement | null>(null);
  const savedOffsets = useRef<{ start: number, end: number } | null>(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [hoverToolbarVisible, setHoverToolbarVisible] = useState(false);
  const [hoverToolbarPosition, setHoverToolbarPosition] = useState({ top: 0, left: 0 });
  const [hoveredLink, setHoveredLink] = useState<HTMLAnchorElement | null>(null);
  const [url, setUrl] = useState("");
  const urlInputRef = useRef<HTMLInputElement>(null);

  const setFormValue = (key: string, value: string): void => {
    if (formState) {
      formState.setValue(key, value)
    }
  }

  const saveCurrentRange = (range: Range) => {
    savedRange.current = range.cloneRange();
    const container = range.commonAncestorContainer;
    const editable = container.nodeType === 3
        ? container.parentElement?.closest('[contenteditable="true"]') as HTMLElement
        : (container as HTMLElement)?.closest('[contenteditable="true"]') as HTMLElement;

    if (editable) {
      savedContainer.current = editable;
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editable);
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      const start = preCaretRange.toString().length;
      const end = start + range.toString().length;
      savedOffsets.current = { start, end };
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection) return;
    
    let rangeRestored = false;

    if (savedRange.current && document.body.contains(savedRange.current.startContainer)) {
      const container = savedRange.current.commonAncestorContainer;
      const editable = container.nodeType === 3
        ? container.parentElement?.closest('[contenteditable="true"]')
        : (container as HTMLElement)?.closest('[contenteditable="true"]');
      
      if (editable) {
        (editable as HTMLElement).focus();
      }
      
      selection.removeAllRanges();
      selection.addRange(savedRange.current);
      rangeRestored = true;
    } else if (savedContainer.current && savedOffsets.current && document.body.contains(savedContainer.current)) {
      const { start, end } = savedOffsets.current;
      const editable = savedContainer.current;
      editable.focus();

      let charIndex = 0, newRange = document.createRange();
      newRange.setStart(editable, 0);
      newRange.collapse(true);
      let nodeStack: Node[] = [editable];
      let node: Node | undefined;
      let foundStart = false;
      let stop = false;

      while (!stop && (node = nodeStack.pop())) {
        if (node.nodeType === 3) {
          const nextCharIndex = charIndex + (node.nodeValue?.length || 0);
          if (!foundStart && start >= charIndex && start <= nextCharIndex) {
            newRange.setStart(node, start - charIndex);
            foundStart = true;
          }
          if (foundStart && end >= charIndex && end <= nextCharIndex) {
            newRange.setEnd(node, end - charIndex);
            stop = true;
          }
          charIndex = nextCharIndex;
        } else {
          let i = node.childNodes.length;
          while (i--) {
            nodeStack.push(node.childNodes[i]);
          }
        }
      }

      selection.removeAllRanges();
      selection.addRange(newRange);
      savedRange.current = newRange.cloneRange();
      rangeRestored = true;
    }

    if (!rangeRestored && savedContainer.current) {
        savedContainer.current.focus();
    }
  };

  const applyLink = () => {
    const finalUrl = urlInputRef.current ? urlInputRef.current.value : url;
    if (!finalUrl.trim()) return;
    
    restoreSelection();
    
    const selection = window.getSelection();
    // Only apply the link if we have a non-collapsed selection
    if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
      document.execCommand("createLink", false, finalUrl.trim());
    } else {
      console.warn("Could not apply link: selection is collapsed or lost.");
    }
    
    setShowLinkInput(false);
    setShowToolbar(false);
    setUrl("");
  };

  const hideHoverToolbar = () => {
    setHoverToolbarVisible(false);
    setHoveredLink(null);
  };

  const clearHoverTimers = () => {
    if (hoverToolbarTimer.current) {
      window.clearTimeout(hoverToolbarTimer.current);
      hoverToolbarTimer.current = null;
    }
    if (hoverHideTimer.current) {
      window.clearTimeout(hoverHideTimer.current);
      hoverHideTimer.current = null;
    }
  };

  const startHideHoverToolbar = () => {
    if (hoverHideTimer.current) {
      window.clearTimeout(hoverHideTimer.current);
    }
    hoverHideTimer.current = window.setTimeout(() => {
      hideHoverToolbar();
      hoverHideTimer.current = null;
    }, 150);
  };

  const updateHoverToolbar = (anchor: HTMLAnchorElement) => {
    const rect = anchor.getBoundingClientRect();
    setHoverToolbarPosition({
      top: Math.max(12, rect.top - 42),
      left: Math.max(12, rect.left),
    });
    setHoveredLink(anchor);
    setHoverToolbarVisible(true);
  };

  const copyLink = async () => {
    if (!hoveredLink?.href) return;
    try {
      await navigator.clipboard.writeText(hoveredLink.href);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = hoveredLink.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const editLink = () => {
    if (!hoveredLink) return;
    const range = document.createRange();
    range.selectNodeContents(hoveredLink);
    saveCurrentRange(range);
    
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    setUrl(hoveredLink.href || "");
    const rect = hoveredLink.getBoundingClientRect();
    setPosition({ top: Math.max(12, rect.top - 46), left: Math.max(12, rect.left) });
    setShowToolbar(true);
    setShowLinkInput(true);
    setHoverToolbarVisible(false);
  };

  const unlink = () => {
    if (!hoveredLink) return;
    const range = document.createRange();
    range.selectNodeContents(hoveredLink);
    saveCurrentRange(range);
    restoreSelection();
    document.execCommand('unlink');
    setShowToolbar(false);
    setShowLinkInput(false);
    hideHoverToolbar();
    setUrl("");
  };

  const handleDescriptionPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    // Access clipboard data
    const clipboardData = e.clipboardData;
    let pastedHtml = clipboardData.getData("text/html") || clipboardData.getData("text/plain");

    // Clean the pasted HTML to strip unwanted styles
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = pastedHtml;

    // Remove all inline styles
    tempDiv.querySelectorAll("*").forEach((el) => {
      el.removeAttribute("style");
    });

    // Get the cleaned HTML
    const cleanedHtml = tempDiv.innerHTML;

    // Insert cleaned HTML into the contentEditable element
    document.execCommand("insertHTML", false, cleanedHtml);

    // Update the form state
    setFormValue("description", e.currentTarget.innerHTML);
  }

  useEffect(() => {
    if ((selectedType === "nameAndSurname" || selectedType === "contactInfo" || selectedType === "address" || selectedType === "domain" || selectedType === 'email') && !editUUID) {
      formState.reset({ ...masterType })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleSelectionChange = () => {
      if (document.activeElement && toolbarRef.current?.contains(document.activeElement)) {
        return;
      }
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.toString().trim() === "") {
        setShowToolbar(false);
        setShowLinkInput(false);
        setUrl("");
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (toolbarRef.current?.contains(event.target as Node)) {
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.toString().trim() === "") {
        setShowToolbar(false);
        setShowLinkInput(false);
        setUrl("");
        return;
      }

      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer.nodeType === 3
        ? range.commonAncestorContainer.parentElement
        : range.commonAncestorContainer as HTMLElement;

      if (!slidesRef.current?.contains(container) || !container?.closest?.('[contenteditable="true"]')) {
        setShowToolbar(false);
        setShowLinkInput(false);
        setUrl("");
        return;
      }

      const anchor = container.closest('a');
      const existingUrl = anchor?.getAttribute('href') || "";
      saveCurrentRange(range);
      const rect = range.getBoundingClientRect();
      setPosition({
        top: rect.top - 46,
        left: Math.max(12, rect.left),
      });
      setUrl(existingUrl);
      setShowToolbar(true);
      setShowLinkInput(false);
      setHoverToolbarVisible(false);
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (toolbarRef.current?.contains(event.target as Node)) {
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.toString().trim() === "") {
        setShowToolbar(false);
        setShowLinkInput(false);
        setUrl("");
        return;
      }

      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer.nodeType === 3
        ? range.commonAncestorContainer.parentElement
        : range.commonAncestorContainer as HTMLElement;

      if (!slidesRef.current?.contains(container) || !container?.closest?.('[contenteditable="true"]')) {
        setShowToolbar(false);
        setShowLinkInput(false);
        setUrl("");
        return;
      }

      const anchor = container.closest('a');
      const existingUrl = anchor?.getAttribute('href') || "";
      saveCurrentRange(range);
      const rect = range.getBoundingClientRect();
      setPosition({
        top: rect.top - 46,
        left: Math.max(12, rect.left),
      });
      setUrl(existingUrl);
      setShowToolbar(true);
      setShowLinkInput(false);
      setHoverToolbarVisible(false);
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (toolbarRef.current?.contains(event.target as Node)) return;
      if (hoverToolbarRef.current?.contains(event.target as Node)) return;

      setShowToolbar(false);
      setShowLinkInput(false);
      hideHoverToolbar();
      setUrl("");
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  useEffect(() => {
    const slideElement = slidesRef.current;
    if (!slideElement) return;

    const clearHoverTimer = () => {
      clearHoverTimers();
    };

    const handleMouseOver = (event: MouseEvent) => {
      if (toolbarRef.current?.contains(event.target as Node) || hoverToolbarRef.current?.contains(event.target as Node)) return;
      if (event.buttons !== 0) return;
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && selection.toString().trim() !== "") return;

      const target = event.target as HTMLElement;
      const anchor = target.closest('a') as HTMLAnchorElement | null;
      if (!anchor || !slidesRef.current?.contains(anchor) || showToolbar) {
        return;
      }

      clearHoverTimers();
      hoverToolbarTimer.current = window.setTimeout(() => {
        updateHoverToolbar(anchor);
      }, 800);
    };

    const handleMouseOut = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');
      const related = event.relatedTarget as Node | null;
      if (anchor && !hoverToolbarRef.current?.contains(related as Node)) {
        startHideHoverToolbar();
      }
    };

    slideElement.addEventListener('mouseover', handleMouseOver);
    slideElement.addEventListener('mouseout', handleMouseOut);

    return () => {
      clearHoverTimers();
      slideElement.removeEventListener('mouseover', handleMouseOver);
      slideElement.removeEventListener('mouseout', handleMouseOut);
    };
  }, [showToolbar]);

  useEffect(() => {
    if (selectedType === "nameAndSurname") {
      formState.setValue('name', 'name')
      if (!formState.getValues('title')) {
        formState.setValue('title', 'What is your name?')
      }

    } else if (selectedType === "address") {
      formState.setValue('name', 'address')
      if (!formState.getValues('title')) {
        formState.setValue('title', 'What is your address?')
      }

    } else if (selectedType === "domain") {
      formState.setValue('name', 'submitter_domain')
      if (!formState.getValues('title')) {
        formState.setValue('title', 'Please provide the URL of your website')
      }
    } else if (selectedType === "email") {
      formState.setValue('name', 'submitter_email')
      if (!formState.getValues('title')) {
        formState.setValue('title', 'Enter your Email')
      }
    } else if (selectedType === "contactInfo") {
      formState.setValue('name', 'contact_info')
      if (!formState.getValues('title')) {
        formState.setValue('title', 'Contact Information')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType])

  const getSlidesStyle = (isFromTemplate?: any) => {
    // using inline style as it is affecting the styles in other places

    if (isFromTemplate) {
      return {
        height: "550px",
        margin: "unset",
      };
    }
    const backgroundImageUrl = landingPage && selectedData.index === -1 ? (dataFromModal?.landingPageBackground ? `url(${dataFromModal?.landingPageBackground})` : "")
      : formState.watch("styles.background") ? `url(${formState.watch("styles.background")})`
        : dataFromModal?.backGround ? `url(${dataFromModal?.backGround})` :
          dataFromModal?.backGround ||
          background
      ;

    return previewType === "pc"
      ? {
        height: "550px",
        margin: "unset",
        backgroundImage: backgroundImageUrl,
      }
      : { backgroundImage: backgroundImageUrl };
  };

  if (landingPage && prevSelected.index === -1) {

    return (
      <div className={`slide-container ${previewType} `} key={selectedData?.index} style={{
        '--color': formState.watch('styles.color') || dataFromModal?.textColor,
        '--background_color': formState.watch('styles.backgroundColor') || dataFromModal?.backgroundColor,
        '--btn_color': dataFromModal?.btnColor,
        '--btn_txt_clr': dataFromModal?.btnTextColor,
        '--font_family': formState.watch('styles.fontFamily') || "Poppins, sans-serif",
        '--landing_btn_color': dataFromModal?.landBtnColor,
        '--landing_btn_txt_color': dataFromModal?.landTextColor,
        '--landing_btn_font': dataFromModal?.landBtnFont,
        '--landing_custom_copy_color': dataFromModal?.landCustomContentColour,
        '--landing_custom_copy_font': dataFromModal?.landCustomContentFont,
        '--loaderColor': dataFromModal?.loaderColor,
      } as React.CSSProperties}>
        <div className="slides" style={getSlidesStyle(false)}>
          <div className='landing-container'>
            {
              dataFromModal?.landContentType === "html" ? (
                <div dangerouslySetInnerHTML={{ __html: dataFromModal?.html || "" }} />
              ) : (
                <div className='landing-page-custom-text-container'>
                  {dataFromModal?.html}
                </div>
              )
            }
            <div className='btn-container' style={{ paddingTop: "0px" }}>
              <button className='open-form-button'>
                {dataFromModal?.landBtnText}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className={`slide-container ${previewType}`} style={{
      '--color': formState.watch('styles.color') || dataFromModal?.textColor,
      '--background_color': dataFromModal?.backgroundColor,
      '--btn_color': dataFromModal?.btnColor,
      '--btn_txt_clr': dataFromModal?.btnTextColor,
      '--font_family': formState.watch('styles.fontFamily') || "Poppins, sans-serif",
      '--landing_btn_color': dataFromModal?.landBtnColor,
      '--landing_btn_txt_color': dataFromModal?.landTextColor,
      '--loaderColor': dataFromModal?.loaderColor,
    } as React.CSSProperties}>
      <div className="slides" style={getSlidesStyle()} ref={slidesRef}>
        {showToolbar && (
          <div ref={toolbarRef} className="selection-toolbar" style={{ top: position.top, left: position.left }}>
            {!showLinkInput && (
              <button type="button" className="toolbar-button link-button" onClick={() => setShowLinkInput(true)} title="Add link">
                <LinkIcon fontSize="small" />
              </button>
            )}
            {showLinkInput && (
              <div className="link-input-panel">
                <input
                  type="text"
                  placeholder="Paste URL"
                  defaultValue={url}
                  ref={urlInputRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyLink();
                    }
                    if (e.key === 'Escape') {
                      setShowLinkInput(false);
                      setShowToolbar(false);
                    }
                  }}
                />
                <button 
                  type="button" 
                  onMouseDown={(e) => e.preventDefault()} 
                  onClick={applyLink}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        )}
        {hoverToolbarVisible && hoveredLink && (
          <div
            ref={hoverToolbarRef}
            className="hover-link-toolbar"
            style={{ top: hoverToolbarPosition.top, left: hoverToolbarPosition.left }}
            onMouseEnter={clearHoverTimers}
            onMouseLeave={startHideHoverToolbar}
          >
            <button type="button" className="toolbar-button" onClick={copyLink} title="Copy link">
              <ContentCopyIcon fontSize="small" />
            </button>
            <button type="button" className="toolbar-button" onClick={editLink} title="Edit link">
              <EditIcon fontSize="small" />
            </button>
            <button type="button" className="toolbar-button" onClick={unlink} title="Unlink">
              <LinkOffIcon fontSize="small" />
            </button>
          </div>
        )}
        <div className='content radio-title' key={inputType + selectedData?.index}>
          {typeof selectedData?.index === "number" && ["select", "multi-select", "nameAndSurname", "contactInfo", "address", "domain", "email", "input", "richText", "multi-input", "date", "dateAndTime", "time", "linkedin", "twitter", "currency", "country", "geolocation", "timezone", "rating", "image-select", "image-multi-select", "google", "openproject", "facebook", "tiktok", "capture"].includes(selectedType) && formState && <FormProvider {...formState}>
            {Object.entries(masterType)?.map(([fields, value], index) => {
              return (
                <div key={inputType + fields + index} className={`title-container ${(fields === "options" && ["image-multi-select", "image-select", "multi-select", "select"].includes(selectedType)) ? "options-image" : ""}`}>
                  {fields === "title" && <h1 key={fields + index} className="title">
                    <span>{selectedData?.index + 1} <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 24 24">
                      <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z" fill={formState.watch('styles.color') || dataFromModal?.textColor} />
                    </svg></span>
                    <div key={inputType + fields + selectedData?.index} {...formState.register(`title`, { required: true })} dangerouslySetInnerHTML={{ __html: formState.getValues(`title`) || value || "" }} contentEditable="true" suppressContentEditableWarning={true} className={`editable`} onFocus={(e: any) => { storeHistory('title', e?.target?.value ?? "", 'slides') }} onInput={(e: any) => { setFormValue("title", e?.currentTarget?.innerHTML) }}></div>
                  </h1>}
                  {fields === "description" && <div
                    key={inputType + fields + selectedData?.index}
                    id='description'
                    {...formState.register(`description`)}
                    dangerouslySetInnerHTML={{ __html: formState.getValues(`description`) || "" }}
                    contentEditable="true"
                    suppressContentEditableWarning={true}
                    onPaste={handleDescriptionPaste}
                    className='description'
                    onFocus={(e: any) => { storeHistory('description', e?.target?.value ?? "", 'slides') }}
                    onInput={(e: any) => { setFormValue("description", e?.currentTarget?.innerHTML) }}  >
                  </div>}
                  {fields === "options" && ["select", "multi-select", "image-select", "image-multi-select"].includes(selectedType) && <Options hasQuantity={methods.watch("isQuantity") || false} key={fields + index} value={value} selectedData={selectedData} selectedType={selectedType} optionIndex={fieldIndex} setOptionIndex={setFieldIndex} storeHistory={storeHistory} setRedoState={setRedoState} />}
                  {fields === "options" && (selectedType === "input" || selectedType === "nameAndSurname" || selectedType === "contactInfo" || selectedType === "address" || selectedType === "domain" || selectedType === 'email') && <Input value={value} selectedData={selectedData} selectedType={selectedType} optionIndex={fieldIndex} setOptionIndex={setFieldIndex} storeHistory={storeHistory} setRedoState={setRedoState} />}
                  {fields === "options" && selectedType === "multi-input" && <Input value={value} selectedData={selectedData} selectedType={selectedType} optionIndex={fieldIndex} setOptionIndex={setFieldIndex} storeHistory={storeHistory} setRedoState={setRedoState} isMultiInput={true} />}
                  {fields === "options" && (selectedType === "date" || selectedType === "dateAndTime") &&
                    <div className='date'><TextField
                      id={formState.watch(`options[${index}].label`) || ""}
                      label={formState.watch(`options[${index}].label`) || ""}
                      type={"date"}
                      disabled={true}
                    />
                    </div>
                  }
                  {fields === "options" && selectedType === "time" && <CustomTimePicker formState={formState} />}
                  {fields === "options" && selectedType === "currency" && <CurrencyInput selectedData={selectedData} selectedType={selectedType} setRedoState={setRedoState} storeHistory={storeHistory} />}
                  {fields === "appointment_id" && <Appointment appointmentId={""} />}
                </div>
              )
            })}
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "dropdown" && formState && <FormProvider {...formState}>
            <h1 className="title">
              <span>{selectedData?.index + 1} <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 24 24">
                <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z" fill={formState.watch('styles.color') || dataFromModal?.textColor} />
              </svg></span>
              <div {...formState.register(`title`, { required: true })} contentEditable="true" suppressContentEditableWarning={true} data-placeholder="Question *" className='editable' onInput={(e: any) => { setFormValue("title", e?.currentTarget?.innerHTML) }} dangerouslySetInnerHTML={{ __html: formState.getValues(`title`) || "" }}  ></div>
            </h1>
            <div
              id='description'
              {...formState.register(`description`)}
              contentEditable="true"
              data-placeholder="Description (optional)"
              suppressContentEditableWarning={true}
              className='description'
              onPaste={handleDescriptionPaste}
              onInput={(e: any) => { setFormValue("description", e?.currentTarget?.innerHTML) }}
              dangerouslySetInnerHTML={{ __html: formState.getValues(`description`) || "" }}
            ></div>
            <div className='dropdown'>
              <div className="dropdown-inputWrapper">
                {!formState.getValues(`isSearchEnabled`) ? <Select
                  disableUnderline={true}
                  disabled={!formState?.getValues('options')?.length || (formState?.getValues('options')?.length === 1 && formState?.getValues('options')[0]?.value === "")}
                  sx={{
                    '& fieldset': { top: 0 }
                  }}
                  variant="standard"
                  MenuProps={{
                    classes: { paper: classes.menuPaper },
                    PaperProps: {
                      sx: {
                        color: "#FFFF",
                        background: "transparent",
                        marginButtom: '10px',
                        borderBottom: '2px dashed #FFFF',
                      },
                    },
                  }}
                >
                  {formState?.getValues('options').map((optionData: any, index: number) => {
                    return <MenuItem key={index} value={optionData?.value} className="Dropdown-menuItem dropdown-responsive-screen">{optionData?.value}</MenuItem>
                  })
                  }
                </Select> :
                  <Autocomplete options={formState?.getValues('options') || []} />}
                <div className="field-line"></div>
              </div>
              <div className='dropdown-btn'>
                <span className='option-count'>{formState?.watch("options")?.filter((obj: any) => obj?.value !== '')?.length || 0} options in list</span>
              </div>
              <Button btn_text='add choices' type='button' onClick={() => { setDropdown(true) }} />
            </div>
            {selectedType === "dropdown" && dropdown && <Popup setRedoState={setRedoState} storeHistory={storeHistory} handleClose={(e: boolean) => { setDropdown(e) }} values={formState?.getValues('options')} />}
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "phoneNumber" && formState && <FormProvider {...formState}>
            <h1 className="title">
              <span>{selectedData?.index + 1} <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 24 24">
                <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z" fill={formState.watch('styles.color') || dataFromModal?.textColor} />
              </svg></span>
              <div {...formState.register(`title`, { required: true })} contentEditable="true" suppressContentEditableWarning={true} data-placeholder="Question *" className='editable' onInput={(e: any) => { setFormValue("title", e?.currentTarget?.innerHTML) }} dangerouslySetInnerHTML={{ __html: formState.getValues(`title`) || "" }} ></div>
            </h1>
            <div
              id='description'
              {...formState.register(`description`)}
              contentEditable="true"
              data-placeholder="Description (optional)"
              suppressContentEditableWarning={true}
              className='description'
              onPaste={handleDescriptionPaste}
              onInput={(e: any) => { setFormValue("description", e?.currentTarget?.innerHTML) }}
              dangerouslySetInnerHTML={{ __html: formState.getValues(`description`) || "" }} >
            </div>
            <div className="input-container">
              <CustomPhoneInput />
            </div>
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "html" && formState && <FormProvider {...formState}>
            <h1 className="title">
              <span>{selectedData?.index + 1} <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 24 24">
                <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z" fill={formState.watch('styles.color') || dataFromModal?.textColor} />
              </svg></span>
              <div className='editable' dangerouslySetInnerHTML={{ __html: formState.watch("title") || "" }} />
            </h1>
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "richText" && formState && <FormProvider {...formState}>
            <TinyMCEEditor />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "geolocation" && formState && <FormProvider {...formState}>
            <GeoLocation />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "calculation" && formState && <FormProvider {...formState}>
            <CalculativeField storeHistory={storeHistory} formState={formState} selectedData={selectedData} dataFromModal={dataFromModal} />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "payment" && formState && <FormProvider {...formState}>
            <BrainTree />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "appointment" && formState && <FormProvider {...formState}>
            <Appointment appointmentId={formState.watch("appointment_id") || ""} />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "linkedin" && formState && <FormProvider {...formState}>
            <LazyImage imgSrc={linkedinButton} className='linkedin-button' />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "twitter" && formState && <FormProvider {...formState}>
            <LazyImage imgSrc={twitterBtnIcon} className='twitter-button' />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "openproject" && formState && <FormProvider {...formState}>
            <LazyImage imgSrc={openProjectBtn} className='openproject-button' />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "tiktok" && formState && <FormProvider {...formState}>
            <LazyImage imgSrc={TiktokButton} className='tiktok-button' />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "google" && formState && <FormProvider {...formState}>
            <LazyImage imgSrc={googleButton} className='google-button' />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "facebook" && formState && <FormProvider {...formState}>
            <LazyImage imgSrc={facebookLoginButton} className='facebook-button' />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "rating" && formState && <FormProvider {...formState}>
            <CustomRating ratingValue={formState?.getValues()} previewType={previewType} />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "country" && formState && <FormProvider {...formState}>
            <CountryInput />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "timezone" && formState && <FormProvider {...formState}>
            <TimeZone />
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "employee" && formState && <FormProvider {...formState}>
            <h1 className="title">
              <span>{selectedData?.index + 1} <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 24 24">
                <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z" fill={formState.watch('styles.color') || dataFromModal?.textColor} />
              </svg></span>
              <div {...formState.register(`title`, { required: true })} contentEditable="true" suppressContentEditableWarning={true} data-placeholder="Question *" className='editable' onInput={(e: any) => { setFormValue("title", e?.currentTarget?.innerHTML) }} dangerouslySetInnerHTML={{ __html: formState.getValues(`title`) || "" }}  ></div>
            </h1>
            <div
              id='description'
              {...formState.register(`description`)}
              contentEditable="true"
              data-placeholder="Description (optional)"
              suppressContentEditableWarning={true}
              className='description'
              onPaste={handleDescriptionPaste}
              onInput={(e: any) => { setFormValue("description", e?.currentTarget?.innerHTML) }}
              dangerouslySetInnerHTML={{ __html: formState.getValues(`description`) || "" }}
            ></div>
            <div className='dropdown'>
              <div className="dropdown-inputWrapper">
                <Select
                  disableUnderline={true}
                  disabled={!formState?.getValues('options')?.length || (formState?.getValues('options')?.length === 1 && formState?.getValues('options')[0]?.value === "")}
                  sx={{
                    '& fieldset': { top: 0 }
                  }}
                  variant="standard"
                  MenuProps={{
                    classes: { paper: classes.menuPaper },
                    PaperProps: {
                      sx: {
                        color: "#FFFF",
                        background: "transparent",
                        marginButtom: '10px',
                        borderBottom: '2px dashed #FFFF',
                      },
                    },
                  }}
                >
                  {formState?.getValues('options').map((optionData: any, index: number) => {
                    return <MenuItem key={index} value={optionData?.value} className="Dropdown-menuItem dropdown-responsive-screen">{optionData?.label}</MenuItem>
                  })
                  }
                </Select>
                <div className="field-line"></div>
              </div>
              <div className='dropdown-btn'>
                <span className='option-count'>{formState?.watch("options")?.filter((obj: any) => obj?.value !== '')?.length || 0} options in list</span>
              </div>
            </div>
          </FormProvider>}
          {typeof selectedData?.index === "number" && selectedType === "capture" && formState && <FormProvider {...formState}>
            <Capture />
          </FormProvider>}
        </div>
      </div >
    </div >
  )
}

export default Slides